import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchChallenge, fetchChallenges, createChallenge, updateChallenge } from "src/actions/mine_craft/challengesActions";

export interface ChallengeInterface {
  url: string;

  id: number;
  app_id: number;
  title: string;
  subtitle: string;
  description: string;
  rules: string;

  thumbnail_url: string;
  ico_url: string;

  addons_count: number;
  likes_count: number;
  popular_count: number;
  comments_count: number;

  filters: string[];

  difficulty: 'easy' | 'medium' | 'hard' | 'expert';
  add_type: 'addon' | 'skin' | 'world' | 'skin_pack' | 'post' | 'structure';
  job_status: 'waiting' | 'processing' | 'done';

  start_at: string;
  end_at: string;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: ChallengeInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<ChallengeInterface>();

const slice = createSlice({
  name: 'challenges',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = { difficulty: 'medium', add_type: 'addon' }

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchChallenges.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchChallenges.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.challenges;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchChallenges.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createChallenge.fulfilled, (state, action) => {
        const challenge = Object.values(action.payload.challenge)[0];
        state.entities[challenge.id] = challenge;
      })
      .addCase(createChallenge.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchChallenge.fulfilled, (state, action) => {
        const challenge = Object.values(action.payload.challenge)[0];
        state.entities[challenge.id] = challenge;
      })

      // #update
      .addCase(updateChallenge.fulfilled, (state, action) => {
        const challenge = Object.values(action.payload.challenge)[0];
        state.entities[challenge.id] = challenge;
      })
      .addCase(updateChallenge.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectChallengeById,
  selectIds: selectChallengeIds,
  selectEntities: selectChallengeEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.challenges);
